import React from 'react';
import styles from './CampoMultiSeletor.module.css';
import Icon from '@/components/Icon';
import {
  mdiCheck,
  mdiChevronDown,
  mdiClose,
  mdiMagnify,
} from '@mdi/js';
import CampoTexto from './CampoTexto';
import ReactDOMServer from 'react-dom/server';
import { Combobox } from '@base-ui/react/combobox';
import { InsideModalContext, getInsideModalZIndex } from '../layout/Modal';

export type CampoMultiSeletorOption = {
  id: string;
  label: React.ReactNode;
  group?: string;
  error?: boolean;
};

type CampoMultiSeletorOptionsProps = {
  selectedIds: string[];
  onChangeSelectedIds: (index: string[]) => void;
  value: CampoMultiSeletorOption[];

  noSearch?: boolean;
  search: string;
  onChangeSearch: (value: string) => void;
};

export const CampoMultiSeletorOptions = ({
  selectedIds,
  onChangeSelectedIds,
  value,
  noSearch,
  search,
  onChangeSearch,
}: CampoMultiSeletorOptionsProps) => {
  const filteredOptions =
    search.length > 0
      ? value?.filter(
        it =>
          !it.label ||
          ReactDOMServer.renderToString(it.label)
            .toLowerCase()
            .includes(search.toLowerCase()),
      )
      : value;

  const groups = filteredOptions?.reduce<{
    [key: string]: CampoMultiSeletorOption[];
  }>((acc, it) => {
    if (it.group) {
      acc[it.group] = acc[it.group] ?? [];
      acc[it.group].push(it);
    } else {
      acc[''] = acc[''] ?? [];
      acc[''].push(it);
    }
    return acc;
  }, {});

  return (
    <div style={{ width: '100%', height: '100%' }}>
      {!noSearch && (
        <CampoTexto
          prepend={
            <Icon path={mdiMagnify} size={1} color="var(--tc-color-gray-400)" />
          }
          placeholder="Pesquisar"
          value={search}
          onChange={onChangeSearch}
        />
      )}
      {search.length && !filteredOptions?.length ? (
        <div className={styles.noResults}>(Nenhum resultado encontrado)</div>
      ) : null}
      {groups
        ? Object.entries(groups).map(([group, gOptions], gidx) => {
          return (
            <div key={gidx}>
              {group && <div className={styles.group}>{group}</div>}
              {gOptions.map((option) => (
                <div
                  tabIndex={0}
                  key={option.id}
                  className={[
                    styles.option,
                    selectedIds.includes(option.id) ? styles.selected : null,
                    group.length > 0 ? styles.grouped : null,
                    option.error ? styles.error : null,
                  ].join(' ')}
                  onClick={() => {
                    onChangeSelectedIds(
                      selectedIds.includes(option.id)
                        ? selectedIds.filter(it => it !== option.id)
                        : [...selectedIds, option.id],
                    );
                  }}
                >
                  <span style={{ minWidth: '32px', marginRight: '4px' }}>
                    <Icon
                      path={selectedIds.includes(option.id) ? mdiCheck : ''}
                      size={1}
                      color="var(--tc-color-primary)"
                    />
                  </span>
                  <span>{option.label}</span>
                </div>
              ))}
            </div>
          );
        })
        : null}
    </div>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function buildLabelMap(options: CampoMultiSeletorOption[]): Record<string, string> {
  const map: Record<string, string> = {};
  for (const opt of options) {
    if (!opt.label) {
      map[opt.id] = '';
    } else if (typeof opt.label === 'string') {
      map[opt.id] = opt.label;
    } else {
      try {
        map[opt.id] = ReactDOMServer.renderToString(opt.label as React.ReactElement);
      } catch {
        map[opt.id] = '';
      }
    }
  }
  return map;
}

function buildGroups(options: CampoMultiSeletorOption[]) {
  return options.reduce<{
    [key: string]: { label?: string; options: CampoMultiSeletorOption[] };
  }>((acc, it) => {
    const key = it.group ?? '';
    if (!acc[key]) acc[key] = { label: it.group, options: [] };
    acc[key].options.push(it);
    return acc;
  }, {});
}

// ─── Props ────────────────────────────────────────────────────────────────────

type CampoMultiSeletorProps = {
  value?: string[];
  onChange?: (value: string[]) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  options: CampoMultiSeletorOption[];
  disabled?: boolean;
  error?: string;
  noSearch?: boolean;
  instanceId?: string;
  [key: string]: any;
};

// ─── Componente ────────────────────────────────────────────────────────────────

export default function CampoMultiSeletor({
  value,
  onChange,
  label,
  placeholder,
  required,
  options = [],
  disabled,
  error,
  noSearch,
  instanceId,
  ...props
}: Readonly<CampoMultiSeletorProps>) {
  const [inputValue, setInputValue] = React.useState('');

  const selectedIds = value ?? [];

  const labelMap = React.useMemo(() => buildLabelMap(options), [options]);

  const filter = (itemId: string, query: string) => {
    const text = labelMap[String(itemId)] ?? '';
    const opt = options.find(o => String(o.id) === String(itemId));
    const groupText = opt?.group ?? '';
    return (
      text.toLowerCase().includes(query.toLowerCase()) ||
      groupText.toLowerCase().includes(query.toLowerCase())
    );
  };

  const groups = React.useMemo(() => buildGroups(options), [options]);
  const hasGroups = Object.keys(groups).some(k => k !== '');
  const insideModal = React.useContext(InsideModalContext);

  const rootItems = React.useMemo(() => {
    if (hasGroups) {
      return Object.entries(groups).map(([key, g]) => ({
        gid: key,
        label: g.label,
        items: g.options.map(o => o.id),
      }));
    }
    return options.map(o => o.id);
  }, [groups, hasGroups, options]);

  const removeSelected = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange?.(selectedIds.filter(it => it !== id));
  };

  return (
    <div style={{ width: '100%', position: 'relative', overflow: 'visible' }} id={instanceId}>
      <Combobox.Root
        multiple
        value={selectedIds}
        onValueChange={(v) => onChange?.(v as string[])}
        inputValue={inputValue}
        onInputValueChange={setInputValue}
        disabled={disabled}
        required={required}
        items={rootItems}
        filter={filter as any}
        itemToStringLabel={(id) => {
          const raw = labelMap[String(id)] ?? '';
          return raw.replace(/<[^>]*>/g, '');
        }}
        modal={false}
      >
        {label && (
          <Combobox.Label className={styles.fieldLabel}>
            {label}
          </Combobox.Label>
        )}

        <Combobox.InputGroup
          className={[
            styles.container,
            error ? styles.containerError : null,
            disabled ? styles.disabled : null,
          ].filter(Boolean).join(' ')}
        >
          <div className={styles.selectedContainer}>
            {selectedIds.length === 0 ? (
              placeholder ? <span className={styles.placeholder}>{placeholder}</span> : null
            ) : (
              <div className={styles.chipsWrapper}>
                {selectedIds.map(id => {
                  const opt = options.find(o => String(o.id) === String(id));
                  if (!opt) return null;
                  return (
                    <div key={id} className={styles.chip}>
                      <div className={styles.chipLabel}>
                        <span>{opt.label}</span>
                        <button
                          type="button"
                          className={styles.chipRemove}
                          onClick={(e) => removeSelected(id, e)}
                        >
                          <Icon path={mdiClose} size={0.7} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <Combobox.Trigger className={styles.triggerOverlay} />

          <div className={styles.arrow}>
            <Icon path={mdiChevronDown} size={1} className={styles.arrowIcon} />
          </div>
        </Combobox.InputGroup>

        <Combobox.Portal>
          <Combobox.Positioner
            className={styles.positioner}
            style={
              insideModal
                ? { zIndex: getInsideModalZIndex(insideModal) }
                : undefined
            }
            side="bottom"
            align="start"
            sideOffset={4}
          >
            <Combobox.Popup className={styles.popup}>
              {!noSearch && (
                <div className={styles.searchWrapper}>
                  <div className={styles.searchInputWrapper}>
                    <Icon path={mdiMagnify} size={1} color="var(--tc-color-gray-400)" />
                    <Combobox.Input className={styles.searchInput} placeholder="Pesquisar" />
                  </div>
                </div>
              )}

              <Combobox.Empty className={styles.noResults}>
                (Nenhum resultado encontrado)
              </Combobox.Empty>

              <Combobox.List className={styles.dropdownContainer}>
                {hasGroups
                  ? (group: { gid: string; label?: string; items: string[] }) => (
                    <Combobox.Group key={group.gid} items={group.items}>
                      {group.gid && (
                        <Combobox.GroupLabel className={`${styles.option} ${styles.group}`}>
                          {group.label}
                        </Combobox.GroupLabel>
                      )}
                      <Combobox.Collection>
                        {(id: string) => {
                          const opt = options.find(o => String(o.id) === String(id));
                          if (!opt) return null;
                          return (
                            <Combobox.Item
                              key={id}
                              value={id}
                              className={({ highlighted, selected }) =>
                                [
                                  styles.option,
                                  styles.grouped,
                                  opt.error ? styles.optionError : null,
                                  highlighted ? styles.highlighted : null,
                                  selected ? styles.selected : null,
                                ].filter(Boolean).join(' ')
                              }
                            >
                              <span className={styles.checkIcon}>
                                {selectedIds.includes(String(id)) && (
                                  <Icon path={mdiCheck} size={0.9} color="var(--tc-color-primary)" />
                                )}
                              </span>
                              {opt.label}
                            </Combobox.Item>
                          );
                        }}
                      </Combobox.Collection>
                    </Combobox.Group>
                  )
                  : (id: string) => {
                    const opt = options.find(o => String(o.id) === String(id));
                    if (!opt) return null;
                    return (
                      <Combobox.Item
                        key={opt.id}
                        value={opt.id}
                        className={({ highlighted, selected }) =>
                          [
                            styles.option,
                            opt.error ? styles.optionError : null,
                            highlighted ? styles.highlighted : null,
                            selected ? styles.selected : null,
                          ].filter(Boolean).join(' ')
                        }
                      >
                        <span className={styles.checkIcon}>
                          {selectedIds.includes(String(opt.id)) && (
                            <Icon path={mdiCheck} size={0.9} color="var(--tc-color-primary)" />
                          )}
                        </span>
                        {opt.label}
                      </Combobox.Item>
                    );
                  }}
              </Combobox.List>
            </Combobox.Popup>
          </Combobox.Positioner>
        </Combobox.Portal>
      </Combobox.Root>
      {error && <div className={styles.errorMessage}>{error}</div>}
    </div>
  );
}
